import { create } from 'zustand';
import { feedbackApi } from '../services/api';
import { FEEDBACK_SESSION_STATUSES, getSessionStatusFilterValue } from '../constants/feedbackSessionStatuses';
import { ASSIGNMENT_STATUSES, getAssignmentStatus } from '../constants/assignmentStatuses';

// Central initial state for easy reset
const initialState = {
  assignments: [],
  isLoading: false,
  isLoadingMore: false,
  error: null,
  pageInfo: {
    endCursor: null,
    hasNextPage: false,
    totalCount: 0
  },
  // Filters
  searchTerm: '',
  selectedStatuses: [],
  selectedReviewTypes: [],
  // UI State
  isReturningFromDetails: false,
  showControls: true,
};

const useMyFeedbackResultsStore = create((set, get) => {
  // Fetch all assignments for the user from API
  const fetchAssignments = async () => {
    if (get().isLoading) {
      return null;
    }

    set({ isLoading: true, error: null });

    try {
      const response = await feedbackApi.getUserAssignments();

      if (response) {
        set({
          assignments: response,
          pageInfo: {
            endCursor: null,
            hasNextPage: false,
            totalCount: response.length
          },
          isLoading: false,
          error: null,
        });
        return response;
      }
      set({ isLoading: false });
      return null;
    } catch (err) {
      set({
        error: err.message || 'Failed to load feedback results',
        isLoading: false,
      });
      return null;
    }
  };

  return {
    ...initialState,

    // UI State Actions
    setIsReturningFromDetails: (flag) => set({ isReturningFromDetails: flag }),
    setShowControls: (show) => set({ showControls: show }),

    // Filter setters
    setSearchTerm: (term) => set({ searchTerm: term }),

    toggleStatus: (status) => {
      const { selectedStatuses } = get();
      // Handle paired status toggle (session + assignment status)
      if (status === FEEDBACK_SESSION_STATUSES.COMPLETED || status === FEEDBACK_SESSION_STATUSES.IN_PROGRESS) {
        const correspondingAssignmentStatus =
          status === FEEDBACK_SESSION_STATUSES.COMPLETED
            ? ASSIGNMENT_STATUSES.COMPLETED
            : ASSIGNMENT_STATUSES.IN_PROGRESS;

        const sessionStatusSelected = selectedStatuses.includes(status);
        const assignmentStatusSelected = selectedStatuses.includes(correspondingAssignmentStatus);

        if (sessionStatusSelected && assignmentStatusSelected) {
          // If both are selected, remove both
          set({ selectedStatuses: selectedStatuses.filter(s => s !== status && s !== correspondingAssignmentStatus) });
        } else {
          // Add any missing statuses
          const newStatuses = [...selectedStatuses];
          if (!sessionStatusSelected) {
            newStatuses.push(status);
          }
          if (!assignmentStatusSelected) {
            newStatuses.push(correspondingAssignmentStatus);
          }
          set({ selectedStatuses: newStatuses });
        }
      } else {
        // For other statuses, just toggle as before
        if (selectedStatuses.includes(status)) {
          set({ selectedStatuses: selectedStatuses.filter(s => s !== status) });
        } else {
          set({ selectedStatuses: [...selectedStatuses, status] });
        }
      }
    },

    toggleReviewType: (reviewType) => {
      const { selectedReviewTypes } = get();
      if (selectedReviewTypes.includes(reviewType)) {
        set({ selectedReviewTypes: selectedReviewTypes.filter(rt => rt !== reviewType) });
      } else {
        set({ selectedReviewTypes: [...selectedReviewTypes, reviewType] });
      }
    },

    // Data actions
    fetchAssignmentsWithReset: () => fetchAssignments(),

    // No-op since API doesn't support pagination
    fetchAssignmentsLoadMore: () => Promise.resolve(null),

    // Filters reset - clear all (no defaults)
    resetFilters: () => {
      set({
        searchTerm: '',
        selectedStatuses: [],
        selectedReviewTypes: [],
      });
    },

    // State reset
    resetState: () => set({
      ...initialState,
    }),

    // Computed: get filtered assignments
    getFilteredAssignments: () => {
      const { assignments, searchTerm, selectedStatuses, selectedReviewTypes } = get();

      return assignments.filter(assignment => {
        const session = assignment.feedbackSession;
        const isIndividualFeedback = !session;

        // For individual feedback, use assignment status
        // For session feedback, use session status
        const status = isIndividualFeedback
          ? getAssignmentStatus(assignment)
          : getSessionStatusFilterValue(session);

        // Search match
        const searchMatch =
          (session?.name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
          (session?.businessUnit?.name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
          (assignment.skillsMatrix?.name?.toLowerCase() || '').includes(searchTerm.toLowerCase());

        // Status match
        const statusMatch = selectedStatuses.length === 0 || selectedStatuses.includes(status);

        // Review type match
        let reviewTypeMatch = true;
        if (selectedReviewTypes.length > 0 && session?.reviewType) {
          reviewTypeMatch = selectedReviewTypes.includes(session.reviewType);
        }

        return searchMatch && statusMatch && reviewTypeMatch;
      });
    },
  };
});

export default useMyFeedbackResultsStore;
